import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, User, 
  CreditCard, Banknote, Smartphone, Building, X,
  Plus, CalendarDays, ArrowRightLeft, XCircle, FileText,
  Package, CheckCircle, AlertCircle, Trash2, ShieldBan, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

// Kiosk API endpoints - configurable for both remote and local
const KIOSK_API_BASE = process.env.REACT_APP_KIOSK_API_BASE || 'https://pickleball-kiosk.plgucamsur.com/api';
const KIOSK_TOKEN_URL = `${KIOSK_API_BASE}/kiosk/token`;
const KIOSK_TRANSACTIONS_URL = `${KIOSK_API_BASE}/transactions`;

// Token cache to avoid fetching on every request
let kioskTokenCache = { token: null, expiresAt: null };

const formatBookingSummary = (bookingInfos) => {
  const infos = Array.isArray(bookingInfos) ? bookingInfos : [];
  if (infos.length === 0) {
    return { courts: ['-'], dates: ['-'], times: ['-'], players: ['-'] };
  }

  const courts = infos.map((info) => (info?.court_no != null ? `Court ${info.court_no}` : info?.court || '-'));
  const dates = infos.map((info) => info?.booking_date || '-');
  const times = infos.map((info) => {
    if (info?.booking_time_start && info?.booking_time_end) {
      return `${info.booking_time_start} - ${info.booking_time_end}`;
    }
    return info?.time_slot || '-';
  });
  const players = infos.map((info) => {
    if (typeof info?.player_count === 'number') return String(info.player_count);
    if (typeof info?.players === 'number') return String(info.players);
    return '-';
  });

  return { courts, dates, times, players };
};

// Helper function to parse time strings in both formats (24-hour and AM/PM)
const parseTimeToHour = (timeStr) => {
  if (!timeStr) return 0;
  
  // Handle 24-hour format like "14:00"
  if (timeStr.includes(':')) {
    return parseInt(timeStr.split(':')[0]);
  }
  
  // Handle AM/PM format like "10AM", "2PM"
  const match = timeStr.match(/(\d+)(AM|PM)/i);
  if (match) {
    let hour = parseInt(match[1]);
    const period = match[2].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return hour;
  }
  
  return parseInt(timeStr) || 0;
};

// Convert time to 24-hour format string for comparison
const normalizeTime = (timeStr) => {
  const hour = parseTimeToHour(timeStr);
  return `${String(hour).padStart(2, '0')}:00`;
};

// Format time for display in PHP-style `h:mm am/pm` (e.g. "8:59 am")
// Accepts: "13:00:00", "13:00", "10AM", "2PM", "08:30 AM"
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return '';

  const raw = String(timeStr).trim();

  // 24-hour formats: HH:MM or HH:MM:SS
  const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hour24 = parseInt(match24[1], 10);
    const minutes = match24[2];
    if (!Number.isFinite(hour24)) hour24 = 0;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  // AM/PM formats: 10AM, 2PM, 08:30 AM
  const matchAmPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (matchAmPm) {
    let hour = parseInt(matchAmPm[1], 10);
    const minutes = matchAmPm[2] || '00';
    const ampm = matchAmPm[3].toLowerCase();
    if (!Number.isFinite(hour)) hour = 0;
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minutes} ${ampm}`;
  }

  // Fallback: if it's something unexpected, try to at least show the hour
  const hour = parseTimeToHour(raw);
  const ampm = hour >= 12 ? 'pm' : 'am';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12}:00 ${ampm}`;
};

// Time slots from 6 AM to 10 PM
const TIME_SLOTS = [];
for (let hour = 6; hour <= 21; hour++) {
  const formatHour = (h) => {
    const hour24 = h % 24;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:00 ${ampm}`;
  };
  TIME_SLOTS.push({
    start: `${String(hour).padStart(2, '0')}:00`,
    end: `${String(hour + 1).padStart(2, '0')}:00`,
    label: `${formatHour(hour)}-${formatHour(hour + 1)}`,
    hour: hour
  });
}

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote },
  { id: 'card', label: 'Card', icon: CreditCard },
  { id: 'ewallet', label: 'eWallet', icon: Smartphone },
  { id: 'online_banking', label: 'Online Banking', icon: Building }
];

const AdminBookingsPage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockOffs, setBlockOffs] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Visual highlight for booking/block-off (orange glow effect)
  const [visualHighlightId, setVisualHighlightId] = useState(null);
  
  // Multi-slot selection (now supports multiple courts and non-consecutive slots)
  const [selectedSlots, setSelectedSlots] = useState([]); // Array of {courtId, timeSlot, court}
  
  // Walk-in booking modal
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInForm, setWalkInForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    selected_addons: {}, // { item_id: quantity }
    transaction_type: 'with_payment', // 'reservation' or 'with_payment'
    payment_method: 'cash',
    notes: ''
  });
  
  // Admin Reserve modal
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveNote, setReserveNote] = useState('');
  
  // Booking details modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [isEditingAddOns, setIsEditingAddOns] = useState(false);
  const [editingAddOns, setEditingAddOns] = useState({});
  
  // Cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationNote, setCancellationNote] = useState('');
  
  // Block-off details modal
  const [showBlockOffModal, setShowBlockOffModal] = useState(false);
  const [selectedBlockOff, setSelectedBlockOff] = useState(null);
  
  // Advanced Admin Reserve modal (standalone)
  const [showAdvancedReserveModal, setShowAdvancedReserveModal] = useState(false);
  const [advancedReserveStep, setAdvancedReserveStep] = useState(1); // 1: Setup, 2: Conflicts, 3: Note
  const [advancedReserveForm, setAdvancedReserveForm] = useState({
    courtSelection: 'all', // 'all' or 'specific'
    selectedCourts: [],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    startTime: '08:00',
    endTime: '17:00',
    note: ''
  });
  const [conflictResults, setConflictResults] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  // Reschedule modal
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '',
    new_court: ''
  });
  
  // Kiosk transactions state
  const [kioskTransactions, setKioskTransactions] = useState([]);
  const [selectedKioskTransaction, setSelectedKioskTransaction] = useState(null);
  const [showKioskModal, setShowKioskModal] = useState(false);
  const [kioskModalLoading, setKioskModalLoading] = useState(false);
  const [showKioskPaymentForm, setShowKioskPaymentForm] = useState(false);
  const [selectedKioskPaymentMethod, setSelectedKioskPaymentMethod] = useState('cash');
  const [kioskPaymentLoading, setKioskPaymentLoading] = useState(false);
  const [showKioskReschedule, setShowKioskReschedule] = useState(false);
  const [showKioskCancel, setShowKioskCancel] = useState(false);
  const [kioskRescheduleForm, setKioskRescheduleForm] = useState({ new_date: '', new_time: '', new_court: '' });
  const [kioskCancelNote, setKioskCancelNote] = useState('');

  // Build an index of kiosk transactions for the selected date so the calendar can render quickly.
  // Supports both payload shapes:
  // - booking_infos[].time_slots: ["10AM", "11AM"]
  // - booking_infos[].booking_time_start/booking_time_end: "13:00:00"-"14:00:00"
  // Merges consecutive bookings from the same customer on the same court
  const kioskSlotMetaByKey = useMemo(() => {
    const map = new Map();
    const txns = Array.isArray(kioskTransactions) ? kioskTransactions : [];
    if (txns.length === 0) return map;

    const courtNumberToId = new Map();
    for (const court of courts) {
      const courtNumber = parseInt(String(court?.name || '').match(/\d+/)?.[0] || '0', 10);
      if (Number.isFinite(courtNumber) && courtNumber > 0) {
        courtNumberToId.set(courtNumber, court.court_id);
      }
    }

    const normalizedSlotsFromBookingInfo = (bookingInfo) => {
      const slots = Array.isArray(bookingInfo?.time_slots) ? bookingInfo.time_slots : [];
      if (slots.length > 0) {
        return slots.map((ts) => normalizeTime(ts)).sort();
      }

      if (bookingInfo?.booking_time_start && bookingInfo?.booking_time_end) {
        const startHour = parseTimeToHour(bookingInfo.booking_time_start);
        const endHour = parseTimeToHour(bookingInfo.booking_time_end);
        if (!Number.isFinite(startHour) || !Number.isFinite(endHour) || endHour <= startHour) return [];

        const out = [];
        for (let h = startHour; h < endHour; h++) {
          out.push(`${String(h).padStart(2, '0')}:00`);
        }
        return out;
      }

      return [];
    };

    for (const txn of txns) {
      const bookingInfos = Array.isArray(txn?.booking_infos) ? txn.booking_infos : [];
      if (bookingInfos.length === 0) continue;

      // Group booking infos by court and merge consecutive time ranges
      const bookingsByCourtDate = bookingInfos
        .filter(b => b?.booking_date === selectedDate)
        .reduce((acc, bookingInfo) => {
          const courtNo = bookingInfo?.court_no ?? bookingInfo?.court_id;
          const courtId = courtNumberToId.get(Number(courtNo));
          if (!courtId) return acc;

          const key = `${courtId}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(bookingInfo);
          return acc;
        }, {});

      // For each court, merge consecutive bookings
      for (const [courtId, courtBookings] of Object.entries(bookingsByCourtDate)) {
        // Get all time slots from all bookings for this court
        const allSlots = courtBookings.flatMap(b => normalizedSlotsFromBookingInfo(b));
        if (allSlots.length === 0) continue;

        // Sort and deduplicate
        const uniqueSortedSlots = [...new Set(allSlots)].sort((a, b) => 
          parseInt(a.split(':')[0], 10) - parseInt(b.split(':')[0], 10)
        );

        // Create a merged booking info with all slots
        const mergedBookingInfo = {
          ...courtBookings[0],
          time_slots: uniqueSortedSlots,
          booking_time_start: uniqueSortedSlots[0],
          booking_time_end: `${String(parseInt(uniqueSortedSlots[uniqueSortedSlots.length - 1].split(':')[0], 10) + 1).padStart(2, '0')}:00`
        };

        // Map each slot
        for (let i = 0; i < uniqueSortedSlots.length; i++) {
          const currentHour = parseInt(uniqueSortedSlots[i].split(':')[0], 10);
          const prevHour = i > 0 ? parseInt(uniqueSortedSlots[i - 1].split(':')[0], 10) : null;
          const isFirst = i === 0 || currentHour - prevHour !== 1;

          let span = 1;
          if (isFirst) {
            for (let j = i + 1; j < uniqueSortedSlots.length; j++) {
              const prevH = parseInt(uniqueSortedSlots[j - 1].split(':')[0], 10);
              const currH = parseInt(uniqueSortedSlots[j].split(':')[0], 10);
              if (currH - prevH === 1) span++;
              else break;
            }
          }

          map.set(`${courtId}|${uniqueSortedSlots[i]}`, {
            txn,
            bookingInfo: mergedBookingInfo,
            isFirst,
            span: isFirst ? span : 1,
          });
        }
      }
    }

    return map;
  }, [kioskTransactions, courts, selectedDate]);

  // Default base rate if no pricing rules match
  const DEFAULT_RATE = 500;

  // Format currency with comma separators and centavos
  const formatCurrency = (amount) => {
    return Number(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get or refresh kiosk token
  const getKioskToken = async () => {
    const now = Date.now();
    
    // Return cached token if still valid (with 60s buffer)
    if (kioskTokenCache.token && kioskTokenCache.expiresAt && kioskTokenCache.expiresAt > now + 60000) {
      return kioskTokenCache.token;
    }

    try {
      const res = await fetch(KIOSK_TOKEN_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ pos_id: 'cwc' }),
        mode: 'cors',
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        console.error('Kiosk token request failed:', res.status, errorText);
        throw new Error(`Failed to get kiosk token: ${res.status}`);
      }

      const data = await res.json();
      console.log('Kiosk token response:', data);
      
      // Response format: { "token": "...", "expires_in": 600 }
      const token = data?.token;
      const expiresIn = data?.expires_in || 600;

      if (!token) {
        console.error('No token in response:', data);
        throw new Error('No token in response');
      }

      // Cache token (expire 30s early to be safe)
      kioskTokenCache = {
        token,
        expiresAt: now + (expiresIn * 1000) - 30000,
      };

      console.log('Kiosk token cached, expires in:', expiresIn, 'seconds');
      return token;
    } catch (err) {
      console.error('Failed to get kiosk token:', err);
      return null;
    }
  };

  // Fetch kiosk transactions
  const fetchKioskTransactions = useCallback(async () => {
    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) return;

      const res = await fetch(KIOSK_TRANSACTIONS_URL, {
        headers: { 
          'X-KIOSK-TOKEN': kioskToken,
        },
      });

      if (!res.ok) {
        console.error('Failed to fetch kiosk transactions:', res.status);
        return;
      }

      const responseData = await res.json();
      console.log('Kiosk transactions response:', responseData);
      const txArray = Array.isArray(responseData?.data) ? responseData.data : [];
      console.log(`Fetched ${txArray.length} kiosk transactions:`, txArray);
      setKioskTransactions(txArray);
    } catch (error) {
      console.error('Failed to fetch kiosk transactions:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [courtsRes, bookingsRes, addOnsRes, blockOffsRes, pricingRes] = await Promise.all([
        fetch(`${API}/admin/courts`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/admin/bookings?date=${selectedDate}&limit=100`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/admin/pos/items`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/admin/block-offs`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/admin/pricing`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);

      if (courtsRes.ok) {
        const courtsData = await courtsRes.json();
        // Show all courts in admin, regardless of status
        setCourts(courtsData);
      }
      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData.bookings || []);
      }
      if (addOnsRes.ok) {
        setAddOns(await addOnsRes.json());
      }
      if (blockOffsRes.ok) {
        setBlockOffs(await blockOffsRes.json());
      }
      if (pricingRes.ok) {
        const pricingData = await pricingRes.json();
        setPricingRules(pricingData.filter(r => r.is_active));
      }
      
      // Fetch kiosk transactions
      await fetchKioskTransactions();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, token, fetchKioskTransactions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get kiosk transaction for a specific court and time slot
  const getKioskTransactionForSlot = (courtId, timeSlot) => {
    const meta = kioskSlotMetaByKey.get(`${courtId}|${timeSlot.start}`);
    if (!meta) return null;
    return { ...meta.txn, currentBookingInfo: meta.bookingInfo, _kioskMeta: meta };
  };

  // Get kiosk transaction span
  const getKioskTransactionSpan = (kioskTxn, currentSlotTime) => {
    if (kioskTxn?._kioskMeta?.span) return kioskTxn._kioskMeta.span;
    if (!kioskTxn?.currentBookingInfo) return 1;

    const info = kioskTxn.currentBookingInfo;
    const slotHour = parseInt(currentSlotTime.split(':')[0], 10);

    const timeSlots = Array.isArray(info?.time_slots) ? info.time_slots : [];
    if (timeSlots.length > 0) {
      const normalizedSlots = timeSlots.map((ts) => normalizeTime(ts)).sort();
      const currentIndex = normalizedSlots.findIndex((s) => parseInt(s.split(':')[0], 10) === slotHour);
      if (currentIndex === -1) return 1;

      let span = 1;
      for (let i = currentIndex + 1; i < normalizedSlots.length; i++) {
        const prevHour = parseInt(normalizedSlots[i - 1].split(':')[0], 10);
        const currHour = parseInt(normalizedSlots[i].split(':')[0], 10);
        if (currHour - prevHour === 1) span++;
        else break;
      }
      return span;
    }

    if (info?.booking_time_start && info?.booking_time_end) {
      const startHour = parseTimeToHour(info.booking_time_start);
      const endHour = parseTimeToHour(info.booking_time_end);
      if (slotHour < startHour || slotHour >= endHour) return 1;
      return Math.max(1, endHour - slotHour);
    }

    return 1;
  };

  // Check if first slot of kiosk transaction
  const isFirstSlotOfKioskTransaction = (courtId, timeSlot) => {
    const meta = kioskSlotMetaByKey.get(`${courtId}|${timeSlot.start}`);
    return Boolean(meta?.isFirst);
  };

  // Handle kiosk transaction click
  const handleKioskTransactionClick = async (transactionCode) => {
    setShowKioskModal(true);
    setKioskModalLoading(true);
    setSelectedKioskTransaction(null);

    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) {
        toast.error('Failed to authenticate with kiosk API');
        setShowKioskModal(false);
        return;
      }

      const url = `${KIOSK_API_BASE}/transactions/${transactionCode}`;
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      setSelectedKioskTransaction(txData);
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      toast.error(`Failed to load transaction: ${error.message}`);
      setShowKioskModal(false);
    } finally {
      setKioskModalLoading(false);
    }
  };

  // Close kiosk modal
  const closeKioskModal = () => {
    setShowKioskModal(false);
    setSelectedKioskTransaction(null);
    setShowKioskPaymentForm(false);
    setShowKioskReschedule(false);
    setShowKioskCancel(false);
    setKioskRescheduleForm({ new_date: '', new_time: '', new_court: '' });
    setKioskCancelNote('');
  };

  // WebSocket connection for realtime kiosk transaction updates (optional - won't break if unavailable)
  useEffect(() => {
    let ws = null;
    let reconnectTimer;
    let reconnectDelayMs = 2000;
    
    const connect = () => {
      try {
        const wsUrl = getWebSocketUrl();
        console.log('🔌 Attempting WebSocket connection:', wsUrl);
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('✅ WebSocket connected for realtime updates');
          reconnectDelayMs = 2000;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📡 WebSocket message received:', data);
            if (data.type === 'kiosk_transaction_updated' || data.type === 'booking_created' || 
                data.type === 'booking_updated' || data.type === 'payment_confirmed') {
              console.log('🔄 Refreshing data...');
              fetchKioskTransactions();
              fetchData();
            }
          } catch (error) {
            console.error('❌ Failed to parse WebSocket message:', error);
          }
        };
        
        ws.onerror = (error) => {
          // WebSocket errors are common if backend isn't running - just log it
          console.warn('⚠️ WebSocket connection error (realtime updates disabled):', error.type);
          console.log('💡 This is normal if backend WebSocket is not available. Page will still work with manual refresh.');
        };
        
        ws.onclose = (event) => {
          if (event.wasClean) {
            console.log('🔌 WebSocket disconnected cleanly');
          } else {
            console.warn('⚠️ WebSocket connection lost, attempting reconnect...');
          }
          // Auto-reconnect
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };
      } catch (error) {
        console.warn('⚠️ WebSocket setup failed (realtime updates disabled):', error.message);
        console.log('💡 Page will continue to work normally with manual refresh.');
      }
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKioskTransactions]);

  // Get price for a specific slot based on pricing rules
  const getPriceForSlot = (courtId, hour) => {
    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayType = isWeekend ? 'weekend' : 'weekday';
    const timeStr = `${String(hour).padStart(2, '0')}:00`;
    
    // Find matching pricing rule
    const matchingRule = pricingRules.find(rule => {
      // Check if rule is active
      if (!rule.is_active) return false;
      
      // Check day type (weekday/weekend)
      if (rule.day_type && rule.day_type !== dayType) return false;
      
      // Check time range
      if (rule.start_time && rule.end_time) {
        const ruleStartHour = parseInt(rule.start_time.split(':')[0]);
        const ruleEndHour = parseInt(rule.end_time.split(':')[0]);
        // Handle end times like "21:59" by treating them as covering the full hour
        const adjustedEndHour = rule.end_time.includes(':59') ? ruleEndHour + 1 : ruleEndHour;
        if (hour < ruleStartHour || hour >= adjustedEndHour) return false;
      }
      
      // Check court selection
      if (rule.court_selection === 'selected' && rule.selected_courts?.length > 0) {
        if (!rule.selected_courts.includes(courtId)) return false;
      }
      
      return true;
    });
    
    return matchingRule ? matchingRule.price_per_hour : DEFAULT_RATE;
  };

  // Calculate total price for all selected slots
  const calculateTotalPrice = () => {
    return selectedSlots.reduce((total, slot) => {
      const price = getPriceForSlot(slot.courtId, slot.timeSlot.hour);
      return total + price;
    }, 0);
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
    setSelectedSlots([]); // Clear selection when changing date
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(format(date, 'yyyy-MM-dd'));
      setSelectedSlots([]);
      setCalendarOpen(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  // Get block-off for a specific court and time slot
  const getBlockOffForSlot = (courtId, timeSlot) => {
    return blockOffs.find(b => {
      if (b.court_id !== courtId || b.date !== selectedDate) return false;
      const blockStartHour = parseInt(b.start_time.split(':')[0]);
      const blockEndHour = parseInt(b.end_time.split(':')[0]);
      const slotHour = parseInt(timeSlot.start.split(':')[0]);
      // Handle end times like "11:59" by treating them as covering the full hour
      const adjustedEndHour = b.end_time.includes(':59') ? blockEndHour + 1 : blockEndHour;
      return slotHour >= blockStartHour && slotHour < adjustedEndHour;
    });
  };

  // Get block-off span (how many slots it covers)
  const getBlockOffSpan = (blockOff) => {
    const startHour = parseInt(blockOff.start_time.split(':')[0]);
    const endHour = parseInt(blockOff.end_time.split(':')[0]);
    // Handle end times like "11:59" by treating them as covering the full hour
    const adjustedEndHour = blockOff.end_time.includes(':59') ? endHour + 1 : endHour;
    return adjustedEndHour - startHour;
  };

  // Check if this is the first slot of a block-off
  const isFirstSlotOfBlockOff = (courtId, timeSlot) => {
    const blockOff = getBlockOffForSlot(courtId, timeSlot);
    if (!blockOff) return false;
    const blockStartHour = parseInt(blockOff.start_time.split(':')[0]);
    const slotHour = parseInt(timeSlot.start.split(':')[0]);
    return blockStartHour === slotHour;
  };

  // Get booking for a specific court and time slot
  const getBookingForSlot = (courtId, timeSlot) => {
    return bookings.find(b => {
      if (b.court_id !== courtId || b.date !== selectedDate) return false;
      if (b.status === 'cancelled') return false; // Skip cancelled bookings
      
      const slotTime = timeSlot.start; // e.g., "11:00"
      
      // Check time_slots array first (for non-consecutive bookings)
      if (b.time_slots && b.time_slots.length > 0) {
        return b.time_slots.includes(slotTime);
      }
      
      // Fallback to legacy start_time/end_time check
      const bookingStartHour = parseTimeToHour(b.start_time);
      const bookingEndHour = parseTimeToHour(b.end_time);
      const slotHour = parseInt(timeSlot.start.split(':')[0]);
      return slotHour >= bookingStartHour && slotHour < bookingEndHour;
    });
  };

  // Get booking span (how many slots it covers) - returns 1 for non-consecutive individual slots
  const getBookingSpan = (booking, currentSlotTime) => {
    // For non-consecutive bookings, check if this slot is part of a consecutive group
    if (booking.time_slots && booking.time_slots.length > 0) {
      const sortedSlots = [...booking.time_slots].sort((a, b) => 
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      
      const currentHour = parseInt(currentSlotTime.split(':')[0]);
      const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === currentHour);
      
      if (currentIndex === -1) return 1;
      
      // Count consecutive slots starting from current slot
      let span = 1;
      for (let i = currentIndex + 1; i < sortedSlots.length; i++) {
        const prevHour = parseInt(sortedSlots[i - 1].split(':')[0]);
        const currHour = parseInt(sortedSlots[i].split(':')[0]);
        if (currHour - prevHour === 1) {
          span++;
        } else {
          break;
        }
      }
      return span;
    }
    
    // Fallback to legacy calculation
    const startHour = parseTimeToHour(booking.start_time);
    const endHour = parseTimeToHour(booking.end_time);
    return endHour - startHour;
  };

  // Check if this is the first slot of a booking (or first of a consecutive group)
  const isFirstSlotOfBooking = (courtId, timeSlot) => {
    const booking = getBookingForSlot(courtId, timeSlot);
    if (!booking) return false;
    
    const slotHour = parseInt(timeSlot.start.split(':')[0]);
    
    // For non-consecutive bookings, check if this is the first of a consecutive group
    if (booking.time_slots && booking.time_slots.length > 0) {
      const sortedSlots = [...booking.time_slots].sort((a, b) => 
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      
      const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
      if (currentIndex === -1) return false;
      
      // It's first if it's the first slot overall, or if the previous slot is not consecutive
      if (currentIndex === 0) return true;
      const prevHour = parseInt(sortedSlots[currentIndex - 1].split(':')[0]);
      return slotHour - prevHour !== 1;
    }
    
    // Fallback to legacy check
    const bookingStartHour = parseTimeToHour(booking.start_time);
    return bookingStartHour === slotHour;
  };

  // Check if a slot is selected
  const isSlotSelected = (courtId, timeSlot) => {
    return selectedSlots.some(s => s.courtId === courtId && s.timeSlot.start === timeSlot.start);
  };

  // Check if a time slot is in the past
  const isSlotInPast = (timeSlot) => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    // If selected date is in the future, no slots are in the past
    if (selectedDate > today) return false;
    
    // If selected date is in the past, all slots are in the past
    if (selectedDate < today) return true;
    
    // If selected date is today, check the time
    const currentHour = now.getHours();
    const slotHour = timeSlot.hour;
    
    // Slot is in the past only if its hour has already passed (current hour is still available)
    return slotHour < currentHour;
  };

  // Handle slot click for multi-selection (supports multiple courts and non-consecutive)
  const handleSlotClick = (court, timeSlot) => {
    const booking = getBookingForSlot(court.court_id, timeSlot);
    const blockOff = getBlockOffForSlot(court.court_id, timeSlot);
    
    // If slot is booked, show booking details
    if (booking) {
      handleBookedSlotClick(booking);
      return;
    }
    
    // If slot is blocked, show block-off details
    if (blockOff) {
      setSelectedBlockOff(blockOff);
      setShowBlockOffModal(true);
      return;
    }
    
    // If slot is in the past, don't allow selection
    if (isSlotInPast(timeSlot)) {
      toast.error('Cannot select past time slots');
      return;
    }
    
    const isSelected = isSlotSelected(court.court_id, timeSlot);
    
    if (isSelected) {
      // Deselect this slot
      setSelectedSlots(prev => prev.filter(s => !(s.courtId === court.court_id && s.timeSlot.start === timeSlot.start)));
    } else {
      // Add to selection (allow any court and any time)
      setSelectedSlots(prev => [...prev, { courtId: court.court_id, timeSlot, court }]);
    }
  };

  // Get selected slots grouped by court
  const getSelectedSlotsByCourt = () => {
    const grouped = {};
    selectedSlots.forEach(s => {
      if (!grouped[s.courtId]) {
        grouped[s.courtId] = { court: s.court, slots: [] };
      }
      grouped[s.courtId].slots.push(s.timeSlot);
    });
    // Sort slots within each court
    Object.values(grouped).forEach(g => {
      g.slots.sort((a, b) => a.hour - b.hour);
    });
    return grouped;
  };

  // Get summary of selection for display
  const getSelectionSummary = () => {
    const grouped = getSelectedSlotsByCourt();
    const courts = Object.values(grouped);
    const totalHours = selectedSlots.length;
    return { courts, totalHours };
  };

  // Open walk-in modal with selected slots
  const openWalkInModal = () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }
    
    setWalkInForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      selected_addons: {},
      transaction_type: 'with_payment',
      payment_method: 'cash',
      notes: ''
    });
    setShowWalkInModal(true);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedSlots([]);
  };

  // Handle booked slot click
  const handleBookedSlotClick = async (booking) => {
    try {
      // Fetch fresh booking details to ensure we have the latest data including add-ons
      const response = await fetch(`${API}/admin/bookings/${booking.booking_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const freshBooking = await response.json();
        setSelectedBooking(freshBooking);
        setBookingNotes(freshBooking.notes || '');
      } else {
        // Fallback to the booking from the list if API call fails
        setSelectedBooking(booking);
        setBookingNotes(booking.notes || '');
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Fallback to the booking from the list
      setSelectedBooking(booking);
      setBookingNotes(booking.notes || '');
    }

    setShowPaymentForm(false);
    setIsEditingAddOns(false);
    setEditingAddOns({});
    setShowBookingModal(true);
  };

  // Start editing add-ons
  const startEditingAddOns = () => {
    // Initialize editing state from current booking add-ons
    const currentAddOns = {};
    if (selectedBooking?.add_ons) {
      selectedBooking.add_ons.forEach(addon => {
        currentAddOns[addon.item_id] = addon.quantity || 1;
      });
    }
    setEditingAddOns(currentAddOns);
    setIsEditingAddOns(true);
  };

  // Cancel editing add-ons
  const cancelEditingAddOns = () => {
    setIsEditingAddOns(false);
    setEditingAddOns({});
  };

  // Save edited add-ons
  const saveEditedAddOns = async () => {
    try {
      const addOnsArray = Object.entries(editingAddOns)
        .filter(([_, qty]) => qty > 0)
        .map(([itemId, quantity]) => ({
          item_id: itemId,
          quantity: quantity
        }));

      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/add-ons`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ add_ons: addOnsArray })
      });

      if (res.ok) {
        toast.success('Add-ons updated successfully');
        setIsEditingAddOns(false);
        setEditingAddOns({});
        fetchData();
        // Update the selected booking with new add-ons
        setSelectedBooking(prev => ({ ...prev, add_ons: addOnsArray }));
      } else {
        toast.error('Failed to update add-ons');
      }
    } catch (error) {
      toast.error('Error updating add-ons');
    }
  };

  // Calculate editing add-ons total
  const calculateEditingAddOnsTotal = () => {
    return Object.entries(editingAddOns).reduce((sum, [itemId, quantity]) => {
      const item = addOns.find(a => a.item_id === itemId);
      return sum + ((item?.price || 0) * quantity);
    }, 0);
  };

  // Calculate walk-in total using pricing rules
  const calculateWalkInTotal = () => {
    const courtTotal = calculateTotalPrice();
    const addOnsTotal = Object.entries(walkInForm.selected_addons).reduce((sum, [itemId, quantity]) => {
      const item = addOns.find(a => a.item_id === itemId);
      return sum + ((item?.price || 0) * quantity);
    }, 0);
    return courtTotal + addOnsTotal;
  };

  // Calculate total for a specific court's slots
  const calculateCourtTotal = (courtId, slots) => {
    return slots.reduce((total, slot) => {
      return total + getPriceForSlot(courtId, slot.hour);
    }, 0);
  };

  // Process walk-in booking - creates multiple bookings if multiple courts selected
  const processWalkIn = async () => {
    if (!walkInForm.customer_name) {
      toast.error('Please enter customer name');
      return;
    }

    const grouped = getSelectedSlotsByCourt();
    const bookingPromises = [];

    // Determine payment status based on transaction type
    const isReservation = walkInForm.transaction_type === 'reservation';
    const paymentStatus = isReservation ? 'unpaid' : 'paid';
    const paymentMethod = isReservation ? null : walkInForm.payment_method;

    // Create a booking for each court
    for (const [courtId, data] of Object.entries(grouped)) {
      const sortedSlots = data.slots.sort((a, b) => a.hour - b.hour);
      const startHour = sortedSlots[0].hour;
      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
      const courtTotal = calculateCourtTotal(courtId, sortedSlots);
      
      const bookingData = {
        court_id: courtId,
        date: selectedDate,
        start_time: `${String(startHour).padStart(2, '0')}:00`,
        end_time: `${String(endHour).padStart(2, '0')}:00`,
        customer_name: walkInForm.customer_name,
        customer_email: walkInForm.customer_email,
        customer_phone: walkInForm.customer_phone,
        duration_hours: sortedSlots.length,
        total_amount: courtTotal,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        booking_type: 'walk_in',
        add_ons: Object.entries(walkInForm.selected_addons).map(([itemId, quantity]) => ({
          item_id: itemId,
          quantity: quantity
        })),
        notes: walkInForm.notes
      };

      bookingPromises.push(
        fetch(`${API}/admin/bookings/walk-in`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(bookingData)
        })
      );
    }

    try {
      const results = await Promise.all(bookingPromises);
      const allOk = results.every(r => r.ok);
      
      if (allOk) {
        const courtCount = Object.keys(grouped).length;
        const typeLabel = isReservation ? 'Reservation' : 'Walk-in booking';
        toast.success(`${typeLabel}${courtCount > 1 ? 's' : ''} created successfully`);
        setShowWalkInModal(false);
        clearSelection();
        fetchData();
      } else {
        toast.error('Some bookings failed to create');
      }
    } catch (error) {
      toast.error('Error processing walk-in');
    }
  };

  // Update booking notes
  const updateBookingNotes = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/notes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: bookingNotes })
      });

      if (res.ok) {
        toast.success('Notes updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update notes');
    }
  };

  // Cancel booking
  // Open cancel modal
  const openCancelModal = () => {
    setCancellationNote('');
    setShowCancelModal(true);
  };

  // Process cancellation
  const cancelBooking = async () => {
    if (!cancellationNote.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/cancel`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cancellation_reason: cancellationNote.trim()
        })
      });

      if (res.ok) {
        toast.success('Booking cancelled');
        setShowCancelModal(false);
        setShowBookingModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  // Update payment status (mark as paid)
  const updatePaymentStatus = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_status: 'paid',
          payment_method: selectedPaymentMethod
        })
      });

      if (res.ok) {
        toast.success('Payment recorded successfully');
        setShowPaymentForm(false);
        // Update the selected booking locally
        setSelectedBooking(prev => ({
          ...prev,
          payment_status: 'paid',
          payment_method: selectedPaymentMethod
        }));
        fetchData();
      } else {
        toast.error('Failed to update payment');
      }
    } catch (error) {
      toast.error('Error updating payment');
    }
  };

  // Confirm kiosk payment
  const confirmKioskPayment = async (transactionCode, paymentMethod) => {
    if (kioskPaymentLoading) return false; // Prevent multiple calls
    
    // Check if already paid
    if (selectedKioskTransaction?.payment_status === 'paid') {
      toast.info('Payment already confirmed');
      return true;
    }
    
    setKioskPaymentLoading(true);
    try {
      // Use backend endpoint instead of calling kiosk API directly
      const res = await fetch(`${API}/admin/payments/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reference_no: transactionCode, // The backend expects reference_no but for kiosk it's transaction_code
          transaction_code: transactionCode,
          payment_method: paymentMethod
        })
      });

      if (res.ok) {
        const data = await res.json();
        
        // Save the confirmed transaction to MongoDB (backend should handle this, but keep for now)
        try {
          const transactionData = selectedKioskTransaction;
          const summary = {
            courts: transactionData.booking_infos?.map(info => 
              info?.court_no != null ? `Court ${info.court_no}` : info?.court || '-'
            ).join(', ') || '-',
            dates: transactionData.booking_infos?.map(info => info?.booking_date || '-').join(', ') || '-',
            times: transactionData.booking_infos?.map(info => {
              if (info?.booking_time_start && info?.booking_time_end) {
                return `${info.booking_time_start} - ${info.booking_time_end}`;
              }
              return info?.time_slot || '-';
            }).join(', ') || '-',
            players: transactionData.booking_infos?.map(info => 
              info?.player_count || info?.players || '-'
            ).join(', ') || '-'
          };

          const transactionRecord = {
            transaction_code: transactionCode,
            customer_name: transactionData.name || '-',
            contact: transactionData.contact_number || '-',
            court: summary.courts,
            players: summary.players,
            booking_date: summary.dates,
            time: summary.times,
            payment_type: paymentMethod,
            payment_status: 'paid',
            total: Number(transactionData.total_amount || 0)
          };

          const saveResponse = await fetch(`${API}/admin/kiosk-transactions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(transactionRecord)
          });

          if (!saveResponse.ok) {
            console.error('Failed to save transaction to database');
            toast.warning('Payment confirmed but not saved to database');
          } else {
            console.log('Transaction saved to MongoDB successfully');
          }
        } catch (saveError) {
          console.error('Error saving transaction:', saveError);
          // Don't fail the whole operation if saving fails
        }
        
        toast.success('Payment confirmed successfully');
        return true;
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.message || 'Failed to confirm payment');
        return false;
      }
    } catch (error) {
      console.error('Error confirming kiosk payment:', error);
      toast.error('Error confirming payment');
      return false;
    } finally {
      setKioskPaymentLoading(false);
    }
  };

  // Open reschedule modal
  const openReschedule = () => {
    setRescheduleForm({
      new_date: selectedBooking.date,
      new_time: selectedBooking.start_time,
      new_court: selectedBooking.court_id
    });
    setShowRescheduleModal(true);
  };

  // Process reschedule
  const processReschedule = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/reschedule`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rescheduleForm)
      });

      if (res.ok) {
        toast.success('Booking rescheduled');
        setShowRescheduleModal(false);
        setShowBookingModal(false);
        fetchData();
      } else {
        toast.error('Failed to reschedule');
      }
    } catch (error) {
      toast.error('Error rescheduling booking');
    }
  };

  // Open Admin Reserve modal
  const openReserveModal = () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }
    setReserveNote('');
    setShowReserveModal(true);
  };

  // Process Admin Reserve (block-off)
  const processAdminReserve = async () => {
    if (!reserveNote.trim()) {
      toast.error('Please enter a note for this reservation');
      return;
    }

    const grouped = getSelectedSlotsByCourt();
    const reservationPromises = [];

    // Create reservations for each court, grouping consecutive slots
    for (const [courtId, data] of Object.entries(grouped)) {
      const sortedSlots = data.slots.sort((a, b) => a.hour - b.hour);
      
      // Find consecutive groups of slots
      const consecutiveGroups = [];
      let currentGroup = [sortedSlots[0]];
      
      for (let i = 1; i < sortedSlots.length; i++) {
        const prevSlot = sortedSlots[i - 1];
        const currentSlot = sortedSlots[i];
        
        // Check if consecutive (difference of 1 hour)
        if (currentSlot.hour - prevSlot.hour === 1) {
          currentGroup.push(currentSlot);
        } else {
          // Non-consecutive, start a new group
          consecutiveGroups.push(currentGroup);
          currentGroup = [currentSlot];
        }
      }
      // Don't forget the last group
      consecutiveGroups.push(currentGroup);
      
      // Create a reservation for each consecutive group
      for (const group of consecutiveGroups) {
        const startHour = group[0].hour;
        const endHour = group[group.length - 1].hour + 1;
        
        const reservationData = {
          court_id: courtId,
          date: selectedDate,
          start_time: `${String(startHour).padStart(2, '0')}:00`,
          end_time: `${String(endHour).padStart(2, '0')}:00`,
          customer_name: 'Admin Reservation',
          customer_email: '',
          customer_phone: '',
          notes: reserveNote.trim(),
          status: 'reservation',
          payment_status: 'unpaid',
          payment_method: 'N/A',
          total_amount: 0,
          source: 'admin_reservation'
        };

        reservationPromises.push(
          fetch(`${API}/admin/bookings`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(reservationData)
          })
        );
      }
    }

    try {
      const results = await Promise.all(reservationPromises);
      const allOk = results.every(r => r.ok);
      
      if (allOk) {
        const reservationCount = reservationPromises.length;
        toast.success(`Admin reservation${reservationCount > 1 ? 's' : ''} created successfully`);
        setShowReserveModal(false);
        clearSelection();
        fetchData();
      } else {
        toast.error('Some reservations failed to create');
      }
    } catch (error) {
      toast.error('Error creating admin reservation');
    }
  };

  // Delete block-off
  const deleteBlockOff = async () => {
    if (!selectedBlockOff) return;
    if (!confirm('Are you sure you want to remove this admin reservation?')) return;
    
    try {
      const res = await fetch(`${API}/admin/block-offs/${selectedBlockOff.block_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Admin reservation removed');
        setShowBlockOffModal(false);
        setSelectedBlockOff(null);
        fetchData();
      } else {
        toast.error('Failed to remove reservation');
      }
    } catch (error) {
      toast.error('Error removing reservation');
    }
  };

  // Advanced Admin Reserve functions
  const openAdvancedReserve = () => {
    setAdvancedReserveForm({
      courtSelection: 'all',
      selectedCourts: [],
      startDate: selectedDate,
      endDate: selectedDate,
      startTime: '08:00',
      endTime: '17:00',
      note: ''
    });
    setAdvancedReserveStep(1);
    setConflictResults(null);
    setShowAdvancedReserveModal(true);
  };

  const checkConflicts = async () => {
    setCheckingConflicts(true);
    try {
      const courtIds = advancedReserveForm.courtSelection === 'all' 
        ? courts.filter(c => c.status === 'active' || !c.status).map(c => c.court_id)
        : advancedReserveForm.selectedCourts;
      
      if (courtIds.length === 0) {
        toast.error('Please select at least one court');
        setCheckingConflicts(false);
        return;
      }
      
      const res = await fetch(`${API}/admin/block-offs/check-conflicts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          court_ids: courtIds,
          start_date: advancedReserveForm.startDate,
          end_date: advancedReserveForm.endDate,
          start_time: advancedReserveForm.startTime,
          end_time: advancedReserveForm.endTime
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConflictResults(data);
        setAdvancedReserveStep(2);
      } else {
        toast.error('Failed to check conflicts');
      }
    } catch (error) {
      toast.error('Error checking conflicts');
    }
    setCheckingConflicts(false);
  };

  const processAdvancedReserve = async () => {
    if (!advancedReserveForm.note.trim()) {
      toast.error('Please enter a note for the reservation');
      return;
    }
    
    // Get courts without conflicts
    const courtsToBlock = conflictResults?.courts
      .filter(c => !c.has_conflicts)
      .map(c => c.court_id) || [];
    
    if (courtsToBlock.length === 0) {
      toast.error('No courts available to block (all have conflicts)');
      return;
    }
    
    // Generate dates
    const dates = conflictResults?.dates || [advancedReserveForm.startDate];
    
    try {
      const blockOffPromises = [];
      
      for (const courtId of courtsToBlock) {
        for (const date of dates) {
          blockOffPromises.push(
            fetch(`${API}/admin/block-offs`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                court_id: courtId,
                date: date,
                start_time: advancedReserveForm.startTime,
                end_time: advancedReserveForm.endTime,
                reason: advancedReserveForm.note.trim()
              })
            })
          );
        }
      }
      
      const results = await Promise.all(blockOffPromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} block-off${successCount > 1 ? 's' : ''} successfully`);
        setShowAdvancedReserveModal(false);
        fetchData();
      } else {
        toast.error('Failed to create block-offs');
      }
    } catch (error) {
      toast.error('Error creating block-offs');
    }
  };

  const selectionSummary = getSelectionSummary();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Court Availability</h1>
            <p className={`${theme.textMuted} mt-1`}>Click time slots to select • Click again to deselect</p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 ${theme.card} rounded-lg p-1`}>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => navigateDate(-1)}
                className={`h-8 w-8 ${theme.textMuted}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`px-3 py-1 ${theme.text} font-medium min-w-[160px] ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    {formatDisplayDate(selectedDate)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-auto p-0 ${theme.card}`} align="center">
                  <CalendarComponent
                    mode="single"
                    selected={new Date(selectedDate)}
                    onSelect={handleDateSelect}
                    initialFocus
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => navigateDate(1)}
                className={`h-8 w-8 ${theme.textMuted}`}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button 
              onClick={openAdvancedReserve}
              className={`${isDarkMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
              data-testid="admin-reserve-btn"
            >
              <ShieldBan className="h-4 w-4 mr-2" />
              Admin Reserve
            </Button>
          </div>
        </div>

        {/* Selection Bar - Shows when slots are selected */}
        {selectedSlots.length > 0 && (
          <Card className={theme.selectionBar}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1">
                  <p className={`${theme.selectionText} font-semibold`}>
                    {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected ({selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''})
                  </p>
                  <div className={`${theme.selectionTextMuted} text-sm space-y-1 mt-1`}>
                    {selectionSummary.courts.map(({ court, slots }) => (
                      <div key={court.court_id}>
                        <span className="font-medium">{court.name}:</span>{' '}
                        {slots.map(s => s.label.split('-')[0]).join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    className={`${isDarkMode ? 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                    onClick={clearSelection}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Selection
                  </Button>
                  <Button 
                    variant="outline"
                    className={`${isDarkMode ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/20' : 'border-amber-600 text-amber-700 hover:bg-amber-50'}`}
                    onClick={openReserveModal}
                  >
                    <ShieldBan className="h-4 w-4 mr-2" />
                    Admin Reserve
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={openWalkInModal}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Process Walk-in
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar Grid */}
        <Card className={`${theme.card} overflow-hidden`}>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse table-fixed">
                  <thead>
                    <tr className={`border-b ${theme.calendarBorder}`}>
                      <th className={`p-4 text-left text-sm font-medium ${theme.calendarTimeText} w-28 ${theme.calendarTimeBg} sticky left-0`}>
                        TIME
                      </th>
                      {courts.map((court) => (
                        <th key={court.court_id} className={`p-4 text-center text-sm font-medium ${theme.tableCell} ${theme.calendarTimeBg}`} style={{ width: `${(100 - 10) / courts.length}%` }}>
                          <div className="font-semibold">{court.name.toUpperCase()}</div>
                          <div className={`text-xs ${theme.textSubtle} font-normal`}>PICKLEBALL</div>
                          <div className={`text-xs font-normal mt-1 ${
                            court.status === 'active' ? 'text-emerald-500' :
                            court.status === 'maintenance' ? 'text-amber-500' :
                            court.status === 'inactive' ? 'text-rose-500' :
                            'text-emerald-500'
                          }`}>
                            {court.status === 'active' || !court.status ? '● Active' :
                             court.status === 'maintenance' ? '● Maintenance' :
                             '● Inactive'}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* TODO: TIMESLOT BUTTON */}
                    {TIME_SLOTS.map((slot) => (
                      <tr key={slot.start} className={`border-b ${theme.calendarRowBorder}`}>
                        <td className={`p-3 text-sm ${theme.calendarTimeText} ${theme.calendarTimeBg} font-medium sticky left-0`}>
                          {slot.label}
                        </td>
                        {courts.map((court) => {
                          const booking = getBookingForSlot(court.court_id, slot);
                          const blockOff = getBlockOffForSlot(court.court_id, slot);
                          const kioskTxn = getKioskTransactionForSlot(court.court_id, slot);
                          const isFirstSlotBooking = isFirstSlotOfBooking(court.court_id, slot);
                          const isFirstSlotBlock = isFirstSlotOfBlockOff(court.court_id, slot);
                          const isFirstSlotKiosk = isFirstSlotOfKioskTransaction(court.court_id, slot);
                          const bookingSpan = booking ? getBookingSpan(booking, slot.start) : 1;
                          const blockSpan = blockOff ? getBlockOffSpan(blockOff) : 1;
                          const kioskSpan = kioskTxn ? getKioskTransactionSpan(kioskTxn, slot.start) : 1;
                          const isSelected = isSlotSelected(court.court_id, slot);
                          const isCourtAvailable = court.status === 'active' || !court.status;
                          const isPast = isSlotInPast(slot);
                          
                          // Skip rendering if this cell is covered by a multi-hour booking from a previous row
                          if (booking && !isFirstSlotBooking) {
                            return null;
                          }
                          
                          // Skip rendering if this cell is covered by a multi-hour block-off from a previous row
                          // The cell should be skipped regardless of whether there's also a booking
                          // (because the block-off's rowSpan from the first slot already covers this cell)
                          if (blockOff && !isFirstSlotBlock) {
                            return null;
                          }
                          
                          // Skip if covered by kiosk transaction
                          if (kioskTxn && !isFirstSlotKiosk) {
                            return null;
                          }
                          
                          return (
                            <td 
                              key={court.court_id} 
                              className="p-1 relative"
                              rowSpan={booking ? bookingSpan : (kioskTxn && isFirstSlotKiosk ? kioskSpan : (blockOff && isFirstSlotBlock ? blockSpan : 1))}
                              style={{ height: '72px', verticalAlign: 'top' }}
                            >
                              {kioskTxn && isFirstSlotKiosk ? (
                                <div 
                                  onClick={() => handleKioskTransactionClick(kioskTxn.transaction_code)}
                                  className={`h-full ${
                                    kioskTxn.payment_status?.toLowerCase() === 'paid'
                                      ? (isDarkMode ? 'bg-emerald-500/20 border-2 border-emerald-400/50' : 'bg-emerald-100 border-2 border-emerald-400')
                                      : (isDarkMode ? 'bg-blue-500/20 border-2 border-blue-400/50' : 'bg-blue-100 border-2 border-blue-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(kioskSpan * 72) - 8}px` }}
                                >
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-emerald-400' : 'text-blue-400') : 'text-gray-900'} font-semibold text-xs leading-tight`}>
                                    {(() => {
                                      const info = kioskTxn.currentBookingInfo;
                                      // Display the full merged time range
                                      if (info?.time_slots && info.time_slots.length > 0) {
                                        const normalizedSlots = info.time_slots.map(ts => normalizeTime(ts)).sort();
                                        const slotHour = parseInt(slot.start.split(':')[0]);
                                        const currentIndex = normalizedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
                                        
                                        if (currentIndex !== -1) {
                                          // Find the end of consecutive slots from current position
                                          let endHour = slotHour + 1;
                                          for (let i = currentIndex + 1; i < normalizedSlots.length; i++) {
                                            const prevH = parseInt(normalizedSlots[i - 1].split(':')[0]);
                                            const currH = parseInt(normalizedSlots[i].split(':')[0]);
                                            if (currH - prevH === 1) {
                                              endHour = currH + 1;
                                            } else {
                                              break;
                                            }
                                          }
                                          return `${formatTimeDisplay(slot.start)} - ${formatTimeDisplay(`${String(endHour).padStart(2, '0')}:00`)}`;
                                        }
                                      }

                                      if (info?.booking_time_start && info?.booking_time_end) {
                                        return `${formatTimeDisplay(info.booking_time_start)} - ${formatTimeDisplay(info.booking_time_end)}`;
                                      }

                                      return formatTimeDisplay(slot.start);
                                    })()}
                                  </div>
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-emerald-300' : 'text-blue-300') : 'text-gray-800'} text-xs mt-1 leading-tight`}>
                                    Court Rental: Online Booking
                                  </div>
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-emerald-200' : 'text-blue-200') : 'text-gray-700'} text-xs font-medium mt-1 leading-tight truncate`}>
                                    {kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'Paid:' : 'Reservation:'} {kioskTxn.name || 'Guest'}
                                  </div>
                                  {(() => {
                                    // Show all courts/times if customer has multiple bookings on the same date
                                    const allBookings = (kioskTxn.booking_infos || []).filter(b => b.booking_date === selectedDate);
                                    if (allBookings.length > 1) {
                                      const courtNos = allBookings.map(b => b.court_no || b.court_id).filter(c => c != null);
                                      const uniqueCourts = [...new Set(courtNos)].sort((a, b) => a - b);
                                      return (
                                        <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-emerald-200' : 'text-blue-200') : 'text-gray-700'} text-[10px] opacity-90 leading-tight mt-1`}>
                                          <div className="font-semibold">Multiple Courts:</div>
                                          <div className="truncate">Courts {uniqueCourts.join(', ')}</div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-emerald-200' : 'text-blue-200') : 'text-gray-700'} text-[10px] opacity-75 leading-tight truncate`}>
                                        ({kioskTxn.contact_number || '-'})
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : booking ? (
                                <div 
                                  onClick={() => handleBookedSlotClick(booking)}
                                  className={`h-full ${
                                    visualHighlightId === booking.booking_id
                                      ? 'bg-orange-500/40 border-2 border-orange-500 ring-4 ring-orange-500/50 animate-pulse'
                                      : (booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved')
                                        ? (isDarkMode ? 'bg-blue-500/20 border-2 border-blue-400/50' : 'bg-blue-100 border-2 border-blue-400')
                                        : (isDarkMode ? 'bg-emerald-500/20 border-2 border-emerald-400/50' : 'bg-emerald-100 border-2 border-emerald-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(bookingSpan * 72) - 8}px` }}
                                >
                                  <div className={`${isDarkMode ? ((booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved') ? 'text-blue-400' : 'text-emerald-400') : 'text-gray-900'} font-semibold text-xs leading-tight`}>
                                    {/* Show time for this consecutive group */}
                                    {(() => {
                                      if (booking.time_slots && booking.time_slots.length > 0) {
                                        const slotHour = parseInt(slot.start.split(':')[0]);
                                        const sortedSlots = [...booking.time_slots].sort((a, b) => 
                                          parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
                                        );
                                        const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
                                        
                                        // Find the end of this consecutive group
                                        let endHour = slotHour + 1;
                                        for (let i = currentIndex + 1; i < sortedSlots.length; i++) {
                                          const prevH = parseInt(sortedSlots[i - 1].split(':')[0]);
                                          const currH = parseInt(sortedSlots[i].split(':')[0]);
                                          if (currH - prevH === 1) {
                                            endHour = currH + 1;
                                          } else {
                                            break;
                                          }
                                        }
                                        return `${formatTimeDisplay(slot.start)} - ${formatTimeDisplay(`${String(endHour).padStart(2, '0')}:00`)}`;
                                      }
                                      return `${formatTimeDisplay(booking.start_time)} - ${formatTimeDisplay(booking.end_time)}`;
                                    })()}
                                  </div>
                                  <div className={`${isDarkMode ? ((booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved') ? 'text-blue-300' : 'text-emerald-300') : 'text-gray-800'} text-xs mt-1 leading-tight flex items-center gap-1`}>
                                    {booking.booking_type === 'walk_in' ? (
                                      <>
                                        <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-amber-500/20 text-amber-500">Walk-in</span>
                                        Court Rental
                                      </>
                                    ) : (
                                      <>
                                        <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-purple-500/20 text-purple-400">Web</span>
                                        Online Booking
                                      </>
                                    )}
                                  </div>
                                  <div className={`${isDarkMode ? ((booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved') ? 'text-blue-200' : 'text-emerald-200') : 'text-gray-700'} text-xs font-medium mt-1 leading-tight truncate`}>
                                    {(booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved')
                                      ? `Reservation: ${booking.customer_name || booking.user?.name || 'Guest'}`
                                      : `Paid: ${booking.customer_name || booking.user?.name || 'Guest'}`
                                    }
                                  </div>
                                  {booking.booking_type !== 'walk_in' && (booking.user?.email || booking.user?.phone || booking.customer_email || booking.customer_phone) && (
                                    <div className={`${isDarkMode ? ((booking.payment_status === 'unpaid' || booking.payment_status === 'pending' || booking.payment_status === 'reserved' || booking.status === 'reserved') ? 'text-blue-200' : 'text-emerald-200') : 'text-gray-600'} text-[10px] opacity-75 leading-tight truncate`}>
                                      {booking.user?.email || booking.customer_email || booking.user?.phone || booking.customer_phone || ''}
                                    </div>
                                  )}
                                </div>
                              ) : blockOff && isFirstSlotBlock ? (
                                <div 
                                  onClick={() => { setSelectedBlockOff(blockOff); setShowBlockOffModal(true); }}
                                  className={`h-full ${
                                    visualHighlightId === blockOff.block_id
                                      ? 'bg-orange-500/40 border-2 border-orange-500 ring-4 ring-orange-500/50 animate-pulse'
                                      : (isDarkMode ? 'bg-rose-500/20 border-2 border-rose-400/50' : 'bg-rose-100 border-2 border-rose-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(blockSpan * 72) - 8}px` }}
                                >
                                  <div className={`${isDarkMode ? 'text-rose-400' : 'text-gray-900'} font-semibold text-xs leading-tight`}>
                                    {blockOff.start_time.slice(0, 5)} - {blockOff.end_time.slice(0, 5)}
                                  </div>
                                  <div className={`${isDarkMode ? 'text-rose-300' : 'text-gray-800'} text-xs mt-1 flex items-center gap-1 leading-tight`}>
                                    <ShieldBan className="h-3 w-3" />
                                    Admin Reserve
                                  </div>
                                  <div className={`${isDarkMode ? 'text-rose-200' : 'text-gray-700'} text-xs font-medium mt-1 leading-tight`}>
                                    {blockOff.reason}
                                  </div>
                                </div>
                              ) : !isCourtAvailable ? (
                                <div className={`h-[64px] rounded-lg ${isDarkMode ? 'bg-slate-700/30 border-slate-600/30' : 'bg-gray-200 border-gray-300'} border flex items-center justify-center`}>
                                  <span className={`${theme.textSubtle} text-xs`}>Unavailable</span>
                                </div>
                              ) : isPast ? (
                                <div className={`h-[64px] rounded-lg ${theme.calendarPast} border flex items-center justify-center cursor-not-allowed opacity-50`}>
                                  <span className={`${theme.calendarPastText} text-xs`}>Past</span>
                                </div>
                              ) : (
                                <div 
                                  onClick={() => handleSlotClick(court, slot)}
                                  className={`h-[64px] rounded-lg cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-amber-500/30 border-2 border-amber-400' 
                                      : `border ${theme.calendarSlotBorder} ${theme.calendarSlotHover}`
                                  }`}
                                >
                                  {isSelected && (
                                    <div className="h-full flex items-center justify-center">
                                      <CheckCircle className="h-5 w-5 text-amber-500" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Walk-in Booking Modal */}
        {showWalkInModal && selectedSlots.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg ${theme.modalBg} my-8`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Walk-in Booking</CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowWalkInModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Slot Info - Shows all selected courts */}
                <div className={`p-3 ${theme.selectionBar} rounded-lg`}>
                  <p className={`${theme.selectionText} font-medium mb-2`}>
                    {formatDisplayDate(selectedDate)} • {selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1">
                    {selectionSummary.courts.map(({ court, slots }) => {
                      const sortedSlots = slots.sort((a, b) => a.hour - b.hour);
                      const startHour = sortedSlots[0].hour;
                      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
                      const formatHour = (h) => h === 12 ? '12PM' : h > 12 ? `${h - 12}PM` : `${h}AM`;
                      return (
                        <div key={court.court_id} className={`${theme.selectionTextMuted} text-sm`}>
                          <span className="font-semibold">{court.name}:</span> {formatHour(startHour)} - {formatHour(endHour)} ({slots.length}hr)
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.tableCell} flex items-center gap-2`}>
                    <User className="h-4 w-4" /> Customer Information
                  </h3>
                  <div>
                    <Label className={`${theme.textMuted} text-xs`}>Name *</Label>
                    <Input
                      value={walkInForm.customer_name}
                      onChange={(e) => setWalkInForm(p => ({ ...p, customer_name: e.target.value }))}
                      className={theme.input}
                      placeholder="Customer name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>Email</Label>
                      <Input
                        type="email"
                        value={walkInForm.customer_email}
                        onChange={(e) => setWalkInForm(p => ({ ...p, customer_email: e.target.value }))}
                        className={theme.input}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>Phone</Label>
                      <Input
                        value={walkInForm.customer_phone}
                        onChange={(e) => setWalkInForm(p => ({ ...p, customer_phone: e.target.value }))}
                        className={theme.input}
                        placeholder="+63 XXX XXX XXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Add-ons */}
                <div>
                  <Label className={`${theme.textMuted} text-xs mb-2 block`}>Add-ons</Label>
                  <div className={`${theme.input} border rounded-md p-2 max-h-40 overflow-y-auto space-y-1`}>
                    {addOns.length === 0 ? (
                      <p className={`${theme.textSubtle} text-sm p-2`}>No add-ons available</p>
                    ) : (
                      addOns.slice(0, 10).map((item) => (
                        <div key={item.item_id} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'}`}>
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="checkbox"
                              checked={!!walkInForm.selected_addons[item.item_id]}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setWalkInForm(p => ({ 
                                    ...p, 
                                    selected_addons: { ...p.selected_addons, [item.item_id]: 1 }
                                  }));
                                } else {
                                  setWalkInForm(p => {
                                    const newAddons = { ...p.selected_addons };
                                    delete newAddons[item.item_id];
                                    return { ...p, selected_addons: newAddons };
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span className={`${theme.text} text-sm`}>{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {walkInForm.selected_addons[item.item_id] && (
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={walkInForm.selected_addons[item.item_id] || 1}
                                onChange={(e) => {
                                  const qty = Math.max(1, parseInt(e.target.value) || 1);
                                  setWalkInForm(p => ({
                                    ...p,
                                    selected_addons: { ...p.selected_addons, [item.item_id]: qty }
                                  }));
                                }}
                                className={`w-14 h-7 px-2 text-center text-sm rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            )}
                            <span className="text-emerald-500 text-sm w-20 text-right">₱{formatCurrency(item.price)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Transaction Type */}
                <div>
                  <Label className={`${theme.textMuted} text-xs mb-2 block`}>Transaction Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <label 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        walkInForm.transaction_type === 'reservation'
                          ? 'border-amber-500 bg-amber-500/20'
                          : `${isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-300 hover:border-gray-400'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="transaction_type"
                        value="reservation"
                        checked={walkInForm.transaction_type === 'reservation'}
                        onChange={(e) => setWalkInForm(p => ({ ...p, transaction_type: e.target.value }))}
                        className="text-amber-500"
                      />
                      <div>
                        <span className={`${walkInForm.transaction_type === 'reservation' ? 'text-amber-400' : theme.text} font-medium`}>Reservation</span>
                        <p className={`text-xs ${theme.textMuted}`}>Mark as unpaid</p>
                      </div>
                    </label>
                    <label 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        walkInForm.transaction_type === 'with_payment'
                          ? 'border-emerald-500 bg-emerald-500/20'
                          : `${isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-300 hover:border-gray-400'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="transaction_type"
                        value="with_payment"
                        checked={walkInForm.transaction_type === 'with_payment'}
                        onChange={(e) => setWalkInForm(p => ({ ...p, transaction_type: e.target.value }))}
                        className="text-emerald-500"
                      />
                      <div>
                        <span className={`${walkInForm.transaction_type === 'with_payment' ? 'text-emerald-400' : theme.text} font-medium`}>With Payment</span>
                        <p className={`text-xs ${theme.textMuted}`}>Mark as paid</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Payment Method - Only show when With Payment is selected */}
                {walkInForm.transaction_type === 'with_payment' && (
                  <div>
                    <Label className={`${theme.textMuted} text-xs mb-2 block`}>Payment Method</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {PAYMENT_METHODS.map((method) => {
                        const Icon = method.icon;
                        return (
                          <button
                            key={method.id}
                            onClick={() => setWalkInForm(p => ({ ...p, payment_method: method.id }))}
                            className={`p-3 rounded-lg border text-center transition-colors ${
                              walkInForm.payment_method === method.id
                                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-600'
                                : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`
                            }`}
                          >
                            <Icon className="h-5 w-5 mx-auto mb-1" />
                            <span className="text-xs">{method.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className={`${theme.textMuted} text-xs`}>Notes</Label>
                  <textarea
                    value={walkInForm.notes}
                    onChange={(e) => setWalkInForm(p => ({ ...p, notes: e.target.value }))}
                    className={`w-full h-16 px-3 py-2 rounded-md ${theme.input} border resize-none`}
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Total */}
                <div className={`p-4 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <div className="flex justify-between items-center">
                    <span className={theme.tableCell}>Court Rental ({selectionSummary.totalHours}hr)</span>
                    <span className={theme.text}>₱{formatCurrency(calculateTotalPrice())}</span>
                  </div>
                  {Object.keys(walkInForm.selected_addons).length > 0 && (
                    <>
                      {Object.entries(walkInForm.selected_addons).map(([itemId, quantity]) => {
                        const item = addOns.find(a => a.item_id === itemId);
                        if (!item) return null;
                        return (
                          <div key={itemId} className="flex justify-between items-center mt-1">
                            <span className={theme.tableCell}>{item.name} x{quantity}</span>
                            <span className={theme.text}>₱{formatCurrency(item.price * quantity)}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  <div className={`flex justify-between items-center mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                    <span className={`${theme.text} font-semibold`}>Total</span>
                    <span className="text-emerald-500 font-bold text-xl">₱{formatCurrency(calculateWalkInTotal())}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowWalkInModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className={`flex-1 ${walkInForm.transaction_type === 'reservation' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`} 
                    onClick={processWalkIn}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {walkInForm.transaction_type === 'reservation' ? 'Create Reservation' : 'Process Payment'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Booking Details Modal */}
        {showBookingModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Booking Details</CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowBookingModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Booking Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <FileText className="h-4 w-4" /> Booking Information
                  </h3>
                  <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Booking ID</span>
                      <span className={`${theme.text} font-mono text-sm`}>{selectedBooking.booking_id?.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={theme.textMuted}>Source</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedBooking.booking_type === 'walk_in' 
                          ? 'bg-amber-500/20 text-amber-400' 
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {selectedBooking.booking_type === 'walk_in' ? 'Walk-in' : 'Web Booking'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Customer</span>
                      <span className={theme.text}>{selectedBooking.customer_name || selectedBooking.user?.name || 'Guest'}</span>
                    </div>
                    {selectedBooking.booking_type !== 'walk_in' && selectedBooking.user?.email && (
                      <div className="flex justify-between">
                        <span className={theme.textMuted}>Email</span>
                        <span className={`${theme.text} text-sm`}>{selectedBooking.user.email}</span>
                      </div>
                    )}
                    {(selectedBooking.customer_phone || selectedBooking.user?.phone) && (
                      <div className="flex justify-between">
                        <span className={theme.textMuted}>Phone</span>
                        <span className={`${theme.text} text-sm`}>{selectedBooking.customer_phone || selectedBooking.user?.phone}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Court</span>
                      <span className={theme.text}>{courts.find(c => c.court_id === selectedBooking.court_id)?.name || selectedBooking.court_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Date & Time</span>
                      <span className={theme.text}>{selectedBooking.date} • {selectedBooking.start_time} - {selectedBooking.end_time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Status</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        selectedBooking.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                        selectedBooking.status === 'cancelled' ? 'bg-rose-500/20 text-rose-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {selectedBooking.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add-ons Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                      <Package className="h-4 w-4" /> Add-ons
                    </h3>
                    {selectedBooking.payment_status !== 'paid' && selectedBooking.status !== 'cancelled' && !isEditingAddOns && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className={`h-7 text-xs ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
                        onClick={startEditingAddOns}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Edit Items
                      </Button>
                    )}
                  </div>
                  
                  {isEditingAddOns ? (
                    // Edit Mode
                    <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-3`}>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {addOns.map((item) => (
                          <div key={item.item_id} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'hover:bg-slate-600/50' : 'hover:bg-gray-200'}`}>
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="checkbox"
                                checked={!!editingAddOns[item.item_id]}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingAddOns(p => ({ ...p, [item.item_id]: 1 }));
                                  } else {
                                    setEditingAddOns(p => {
                                      const newAddons = { ...p };
                                      delete newAddons[item.item_id];
                                      return newAddons;
                                    });
                                  }
                                }}
                                className="rounded"
                              />
                              <span className={`${theme.text} text-sm`}>{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {editingAddOns[item.item_id] && (
                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  value={editingAddOns[item.item_id] || 1}
                                  onChange={(e) => {
                                    const qty = Math.max(1, parseInt(e.target.value) || 1);
                                    setEditingAddOns(p => ({ ...p, [item.item_id]: qty }));
                                  }}
                                  className={`w-14 h-7 px-2 text-center text-sm rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                />
                              )}
                              <span className="text-emerald-500 text-sm w-20 text-right">₱{formatCurrency(item.price)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Editing Total */}
                      <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                        <span className={`${theme.textMuted} font-medium`}>Add-ons Total</span>
                        <span className="text-emerald-500 font-semibold">₱{formatCurrency(calculateEditingAddOnsTotal())}</span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                          onClick={cancelEditingAddOns}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={saveEditedAddOns}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    selectedBooking.add_ons && selectedBooking.add_ons.length > 0 ? (
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        {selectedBooking.add_ons.map((addon, index) => {
                          const addonItem = addOns.find(a => a.item_id === addon.item_id);
                          const quantity = addon.quantity || 1;
                          const price = addonItem?.price || 0;
                          return (
                            <div key={index} className="flex justify-between">
                              <span className={theme.textMuted}>
                                {addonItem?.name || addon.item_id} x{quantity}
                              </span>
                              <span className={theme.text}>₱{formatCurrency(price * quantity)}</span>
                            </div>
                          );
                        })}
                        <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                          <span className={`${theme.textMuted} font-medium`}>Add-ons Total</span>
                          <span className="text-emerald-500 font-semibold">
                            ₱{formatCurrency(selectedBooking.add_ons.reduce((sum, addon) => {
                              const addonItem = addOns.find(a => a.item_id === addon.item_id);
                              return sum + ((addonItem?.price || 0) * (addon.quantity || 1));
                            }, 0))}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3`}>
                        <p className={`${theme.textSubtle} text-sm text-center`}>No add-ons</p>
                      </div>
                    )
                  )}
                </div>

                {/* Payment Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <CreditCard className="h-4 w-4" /> Payment Information
                  </h3>
                  <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Court Rental</span>
                      <span className={theme.text}>₱{formatCurrency(selectedBooking.total_amount)}</span>
                    </div>
                    {selectedBooking.add_ons && selectedBooking.add_ons.length > 0 && (
                      <div className="flex justify-between">
                        <span className={theme.textMuted}>Add-ons</span>
                        <span className={theme.text}>
                          ₱{formatCurrency(selectedBooking.add_ons.reduce((sum, addon) => {
                            const addonItem = addOns.find(a => a.item_id === addon.item_id);
                            return sum + ((addonItem?.price || 0) * (addon.quantity || 1));
                          }, 0))}
                        </span>
                      </div>
                    )}
                    <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                      <span className={`${theme.textMuted} font-medium`}>Total Amount</span>
                      <span className="text-emerald-500 font-semibold">
                        ₱{formatCurrency(
                          selectedBooking.total_amount + 
                          (selectedBooking.add_ons?.reduce((sum, addon) => {
                            const addonItem = addOns.find(a => a.item_id === addon.item_id);
                            return sum + ((addonItem?.price || 0) * (addon.quantity || 1));
                          }, 0) || 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Payment Method</span>
                      <span className={`${theme.text} capitalize`}>{selectedBooking.payment_method || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={theme.textMuted}>Payment Status</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        selectedBooking.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {selectedBooking.payment_status || 'pending'}
                      </span>
                    </div>
                    
                    {/* Pay Now Button - Only show when unpaid/pending/reserved */}
                    {(selectedBooking.payment_status === 'unpaid' || selectedBooking.payment_status === 'pending' || selectedBooking.payment_status === 'reserved' || selectedBooking.status === 'reserved') && !showPaymentForm && (
                      <Button 
                        size="sm" 
                        className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          setSelectedPaymentMethod('cash');
                          setShowPaymentForm(true);
                        }}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay Now
                      </Button>
                    )}
                    
                    {/* Payment Form - Show when Pay Now is clicked */}
                    {showPaymentForm && (
                      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'} space-y-3`}>
                        <Label className={`${theme.text} text-sm`}>Select Payment Method</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {PAYMENT_METHODS.map((method) => {
                            const Icon = method.icon;
                            return (
                              <button
                                key={method.id}
                                onClick={() => setSelectedPaymentMethod(method.id)}
                                className={`p-2 rounded-lg border text-center transition-colors ${
                                  selectedPaymentMethod === method.id
                                    ? 'border-emerald-500 bg-emerald-500/20 text-emerald-500'
                                    : isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                <Icon className="h-4 w-4 mx-auto mb-1" />
                                <span className="text-xs">{method.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                            onClick={() => setShowPaymentForm(false)}
                          >
                            Cancel
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={updatePaymentStatus}
                          >
                            Save Payment
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <FileText className="h-4 w-4" /> Notes
                  </h3>
                  <textarea
                    value={bookingNotes}
                    onChange={(e) => setBookingNotes(e.target.value)}
                    className={`w-full h-20 px-3 py-2 rounded-md resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                    placeholder="Add notes to this booking..."
                  />
                  <Button size="sm" variant="outline" className={isDarkMode ? 'border-slate-600' : 'border-gray-300'} onClick={updateBookingNotes}>
                    Save Notes
                  </Button>
                </div>

                {/* Action Buttons */}
                {selectedBooking.status !== 'cancelled' && (
                  <div className={`grid grid-cols-2 gap-2 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <Button variant="outline" className={isDarkMode ? 'border-slate-600' : 'border-gray-300'} onClick={openReschedule}>
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      Reschedule
                    </Button>
                    <Button variant="outline" className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10" onClick={openCancelModal}>
                      <XCircle className="h-4 w-4 mr-1" />
                      Cancel Booking
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reschedule Modal */}
        {showRescheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader>
                <CardTitle className={theme.text}>Reschedule Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>New Date</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.new_date}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_date: e.target.value }))}
                    className={theme.input}
                  />
                </div>
                <div>
                  <Label className={theme.text}>New Time</Label>
                  <select
                    value={rescheduleForm.new_time}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_time: e.target.value }))}
                    className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {TIME_SLOTS.map(slot => (
                      <option key={slot.start} value={slot.start}>{slot.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className={theme.text}>New Court</Label>
                  <select
                    value={rescheduleForm.new_court}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_court: e.target.value }))}
                    className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {courts.map(court => (
                      <option key={court.court_id} value={court.court_id}>{court.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowRescheduleModal(false)}>
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={processReschedule}>
                    Confirm
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin Reserve Modal */}
        {showReserveModal && selectedSlots.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowReserveModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Slot Info */}
                <div className={`p-3 ${isDarkMode ? 'bg-amber-500/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border rounded-lg`}>
                  <p className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700'} font-medium mb-2`}>
                    {formatDisplayDate(selectedDate)} • {selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1">
                    {selectionSummary.courts.map(({ court, slots }) => {
                      const sortedSlots = slots.sort((a, b) => a.hour - b.hour);
                      const startHour = sortedSlots[0].hour;
                      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
                      const formatHour = (h) => h === 12 ? '12PM' : h > 12 ? `${h - 12}PM` : `${h}AM`;
                      return (
                        <div key={court.court_id} className={`${isDarkMode ? 'text-amber-300' : 'text-amber-600'} text-sm`}>
                          <span className="font-semibold">{court.name}:</span> {formatHour(startHour)} - {formatHour(endHour)} ({slots.length}hr)
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={`${theme.textMuted} text-sm`}>
                  These time slots will be blocked and unavailable for customer booking.
                </div>

                {/* Note (Required) */}
                <div>
                  <Label className={theme.text}>
                    Note <span className="text-rose-400">*</span>
                  </Label>
                  <textarea
                    value={reserveNote}
                    onChange={(e) => setReserveNote(e.target.value)}
                    className={`w-full h-24 px-3 py-2 rounded-md resize-none mt-1 border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Enter reason for blocking these slots (required)..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowReserveModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={processAdminReserve}
                    disabled={!reserveNote.trim()}
                  >
                    <ShieldBan className="h-4 w-4 mr-2" />
                    Block Slots
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Block-Off Details Modal */}
        {showBlockOffModal && selectedBlockOff && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve Details
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => { setShowBlockOffModal(false); setSelectedBlockOff(null); }} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Block-off Info */}
                <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Court</span>
                    <span className={theme.text}>{courts.find(c => c.court_id === selectedBlockOff.court_id)?.name || selectedBlockOff.court_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Date</span>
                    <span className={theme.text}>{selectedBlockOff.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Time</span>
                    <span className={theme.text}>{selectedBlockOff.start_time} - {selectedBlockOff.end_time}</span>
                  </div>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label className={theme.text}>Note</Label>
                  <div className={`p-3 ${isDarkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-700'} border rounded-lg`}>
                    {selectedBlockOff.reason}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => { setShowBlockOffModal(false); setSelectedBlockOff(null); }}
                  >
                    Close
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                    onClick={deleteBlockOff}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Block
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cancellation Modal */}
        {showCancelModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <XCircle className="h-5 w-5 text-rose-400" />
                  Cancel Booking
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowCancelModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Booking Summary */}
                <div className={`p-3 ${isDarkMode ? 'bg-rose-500/10 border-rose-500/30' : 'bg-rose-50 border-rose-200'} border rounded-lg`}>
                  <p className={`${isDarkMode ? 'text-rose-400' : 'text-rose-700'} font-medium`}>
                    {selectedBooking.customer_name || selectedBooking.user?.name || 'Guest'}
                  </p>
                  <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'} text-sm`}>
                    {courts.find(c => c.court_id === selectedBooking.court_id)?.name} • {selectedBooking.date}
                  </p>
                  <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'} text-sm`}>
                    {selectedBooking.start_time} - {selectedBooking.end_time}
                  </p>
                </div>

                <div className={`${theme.textMuted} text-sm`}>
                  This action cannot be undone. The time slot will become available for booking again.
                </div>

                {/* Cancellation Reason (Required) */}
                <div>
                  <Label className={theme.text}>
                    Cancellation Reason <span className="text-rose-400">*</span>
                  </Label>
                  <textarea
                    value={cancellationNote}
                    onChange={(e) => setCancellationNote(e.target.value)}
                    className={`w-full h-24 px-3 py-2 rounded-md resize-none mt-1 border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Enter reason for cancellation (required)..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => setShowCancelModal(false)}
                  >
                    Go Back
                  </Button>
                  <Button 
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={cancelBooking}
                    disabled={!cancellationNote.trim()}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Confirm Cancellation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Admin Reserve Modal */}
        {showAdvancedReserveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve
                  <span className={`text-sm font-normal ${theme.textMuted}`}>
                    - Step {advancedReserveStep} of 3
                  </span>
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowAdvancedReserveModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Step 1: Setup */}
                {advancedReserveStep === 1 && (
                  <>
                    {/* Court Selection */}
                    <div>
                      <Label className={theme.text}>Court Selection</Label>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => setAdvancedReserveForm(p => ({ ...p, courtSelection: 'all', selectedCourts: [] }))}
                          className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                            advancedReserveForm.courtSelection === 'all'
                              ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                              : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`
                          }`}
                        >
                          All Courts
                        </button>
                        <button
                          onClick={() => setAdvancedReserveForm(p => ({ ...p, courtSelection: 'specific' }))}
                          className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                            advancedReserveForm.courtSelection === 'specific'
                              ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                              : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`
                          }`}
                        >
                          Specific Courts
                        </button>
                      </div>
                      
                      {advancedReserveForm.courtSelection === 'specific' && (
                        <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'border-slate-600 bg-slate-700/30' : 'border-gray-200 bg-gray-50'} max-h-32 overflow-y-auto`}>
                          {courts.filter(c => c.status === 'active' || !c.status).map(court => (
                            <label key={court.court_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${isDarkMode ? 'hover:bg-slate-600/50' : 'hover:bg-gray-100'}`}>
                              <input
                                type="checkbox"
                                checked={advancedReserveForm.selectedCourts.includes(court.court_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAdvancedReserveForm(p => ({ ...p, selectedCourts: [...p.selectedCourts, court.court_id] }));
                                  } else {
                                    setAdvancedReserveForm(p => ({ ...p, selectedCourts: p.selectedCourts.filter(id => id !== court.court_id) }));
                                  }
                                }}
                                className="rounded"
                              />
                              <span className={theme.text}>{court.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Start Date</Label>
                        <Input
                          type="date"
                          value={advancedReserveForm.startDate}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, startDate: e.target.value }))}
                          className={`mt-1 ${theme.input}`}
                        />
                      </div>
                      <div>
                        <Label className={theme.text}>End Date</Label>
                        <Input
                          type="date"
                          value={advancedReserveForm.endDate}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, endDate: e.target.value }))}
                          className={`mt-1 ${theme.input}`}
                        />
                      </div>
                    </div>

                    {/* Time Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Start Time</Label>
                        <select
                          value={advancedReserveForm.startTime}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, startTime: e.target.value }))}
                          className={`w-full h-10 px-3 rounded-md mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        >
                          {TIME_SLOTS.map(slot => (
                            <option key={slot.start} value={slot.start}>{slot.label.split('-')[0]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className={theme.text}>End Time</Label>
                        <select
                          value={advancedReserveForm.endTime}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, endTime: e.target.value }))}
                          className={`w-full h-10 px-3 rounded-md mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        >
                          {TIME_SLOTS.map(slot => (
                            <option key={slot.end} value={slot.end}>{slot.label.split('-')[1]}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowAdvancedReserveModal(false)}>
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={checkConflicts}
                        disabled={checkingConflicts || (advancedReserveForm.courtSelection === 'specific' && advancedReserveForm.selectedCourts.length === 0)}
                      >
                        {checkingConflicts ? 'Checking...' : 'Next: Check Conflicts'}
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 2: Conflict Results */}
                {advancedReserveStep === 2 && conflictResults && (
                  <>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                      <p className={`${theme.text} font-medium`}>
                        {conflictResults.dates.length} day{conflictResults.dates.length > 1 ? 's' : ''} • {conflictResults.time_range}
                      </p>
                      <p className={`${theme.textMuted} text-sm`}>
                        {conflictResults.dates[0]}{conflictResults.dates.length > 1 ? ` to ${conflictResults.dates[conflictResults.dates.length - 1]}` : ''}
                      </p>
                    </div>

                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {conflictResults.courts.map(court => (
                        <div key={court.court_id} className={`p-3 rounded-lg border ${
                          court.has_conflicts 
                            ? (isDarkMode ? 'border-rose-500/50 bg-rose-500/10' : 'border-rose-300 bg-rose-50')
                            : (isDarkMode ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-emerald-300 bg-emerald-50')
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            {court.has_conflicts ? (
                              <AlertCircle className="h-4 w-4 text-rose-400" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-emerald-400" />
                            )}
                            <span className={`font-medium ${court.has_conflicts ? 'text-rose-400' : 'text-emerald-400'}`}>
                              {court.court_name}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              court.has_conflicts 
                                ? (isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-600')
                                : (isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-600')
                            }`}>
                              {court.has_conflicts ? `${court.conflicts.length} conflict${court.conflicts.length > 1 ? 's' : ''}` : 'Clear'}
                            </span>
                          </div>
                          {court.has_conflicts && (
                            <div className="space-y-1 ml-6">
                              {court.conflicts.slice(0, 5).map((conflict, idx) => (
                                <div key={idx} className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                  <span className={`font-medium ${theme.textMuted}`}>{conflict.date}</span>
                                  <span className={theme.textMuted}> • {conflict.time}</span>
                                  <div className={`text-xs ${theme.textSubtle}`}>{conflict.description}</div>
                                </div>
                              ))}
                              {court.conflicts.length > 5 && (
                                <p className={`text-xs ${theme.textSubtle}`}>...and {court.conflicts.length - 5} more</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                      <p className="text-amber-400 font-medium">
                        {conflictResults.courts.filter(c => !c.has_conflicts).length} of {conflictResults.courts.length} courts can be blocked
                      </p>
                      {conflictResults.courts.some(c => c.has_conflicts) && (
                        <p className={`text-sm ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                          Courts with conflicts will be skipped
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setAdvancedReserveStep(1)}>
                        Back
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => setAdvancedReserveStep(3)}
                        disabled={conflictResults.courts.every(c => c.has_conflicts)}
                      >
                        Next: Add Note
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 3: Note */}
                {advancedReserveStep === 3 && (
                  <>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <p className="text-emerald-400 font-medium mb-1">Ready to block:</p>
                      <div className="space-y-1">
                        {conflictResults?.courts.filter(c => !c.has_conflicts).map(court => (
                          <div key={court.court_id} className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                            ✓ {court.court_name}
                          </div>
                        ))}
                      </div>
                      <p className={`text-sm mt-2 ${theme.textMuted}`}>
                        {conflictResults?.dates.length} day{conflictResults?.dates.length > 1 ? 's' : ''} • {conflictResults?.time_range}
                      </p>
                    </div>

                    <div>
                      <Label className={theme.text}>
                        Note <span className="text-rose-400">*</span>
                      </Label>
                      <textarea
                        value={advancedReserveForm.note}
                        onChange={(e) => setAdvancedReserveForm(p => ({ ...p, note: e.target.value }))}
                        className={`w-full h-24 px-3 py-2 rounded-md mt-1 resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        placeholder="Enter reason for blocking these slots (required)..."
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setAdvancedReserveStep(2)}>
                        Back
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={processAdvancedReserve}
                        disabled={!advancedReserveForm.note.trim()}
                      >
                        <ShieldBan className="h-4 w-4 mr-2" />
                        Create Block-offs
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kiosk Transaction Details Modal */}
        {showKioskModal && selectedKioskTransaction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Booking Details</CardTitle>
                <Button size="icon" variant="ghost" onClick={closeKioskModal} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {kioskModalLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  </div>
                ) : (
                  <>
                    {/* Booking Info */}
                    <div className="space-y-3">
                      <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                        <FileText className="h-4 w-4" /> Booking Information
                      </h3>
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Booking ID</span>
                          <span className={`${theme.text} font-mono text-sm`}>{selectedKioskTransaction.transaction_code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Customer</span>
                          <span className={theme.text}>{selectedKioskTransaction.name || 'Guest'}</span>
                        </div>
                        {selectedKioskTransaction.contact_number && (
                          <div className="flex justify-between">
                            <span className={theme.textMuted}>Contact</span>
                            <span className={theme.text}>{selectedKioskTransaction.contact_number}</span>
                          </div>
                        )}
                        {/* Show first court or multiple courts indicator */}
                        {(() => {
                          const bookings = selectedKioskTransaction.booking_infos || [];
                          const courts = [...new Set(bookings.map(b => b.court_no || b.court_id))].sort((a, b) => a - b);
                          return (
                            <div className="flex justify-between">
                              <span className={theme.textMuted}>Court</span>
                              <span className={theme.text}>
                                {courts.length === 1 ? `Court ${courts[0]}` : `${courts.length} Courts (${courts.join(', ')})`}
                              </span>
                            </div>
                          );
                        })()}
                        {/* Show date & time for first booking or summary */}
                        {(() => {
                          const bookings = selectedKioskTransaction.booking_infos || [];
                          if (bookings.length === 1) {
                            const booking = bookings[0];
                            return (
                              <div className="flex justify-between">
                                <span className={theme.textMuted}>Date & Time</span>
                                <span className={theme.text}>
                                  {booking.booking_date} • {
                                    booking.booking_time_start && booking.booking_time_end 
                                      ? `${formatTimeDisplay(booking.booking_time_start)} - ${formatTimeDisplay(booking.booking_time_end)}`
                                      : (booking.time_slots || []).map(t => formatTimeDisplay(t)).join(', ')
                                  }
                                </span>
                              </div>
                            );
                          } else if (bookings.length > 1) {
                            return (
                              <div className="flex justify-between">
                                <span className={theme.textMuted}>Date & Time</span>
                                <span className={theme.text}>{bookings.length} bookings (see below)</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Status</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            selectedKioskTransaction.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedKioskTransaction.payment_status === 'reserved' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {selectedKioskTransaction.payment_status === 'paid' ? 'confirmed' : selectedKioskTransaction.payment_status || 'confirmed'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Multiple Bookings Details (if more than 1 court/time) */}
                    {selectedKioskTransaction.booking_infos && selectedKioskTransaction.booking_infos.length > 1 && (
                      <div className="space-y-3">
                        <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                          <CalendarDays className="h-4 w-4" /> All Bookings
                        </h3>
                        <div className="space-y-2">
                          {selectedKioskTransaction.booking_infos.map((booking, idx) => (
                            <div key={idx} className={`${isDarkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3`}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className={`${theme.text} font-medium text-sm`}>Court {booking.court_no || booking.court_id}</p>
                                  <p className={`${theme.textMuted} text-xs mt-0.5`}>{booking.booking_date}</p>
                                </div>
                                <p className={`${theme.text} text-sm`}>
                                  {booking.booking_time_start && booking.booking_time_end 
                                    ? `${formatTimeDisplay(booking.booking_time_start)} - ${formatTimeDisplay(booking.booking_time_end)}`
                                    : (booking.time_slots || []).map(t => formatTimeDisplay(t)).join(', ')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="space-y-3">
                      <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                        <CreditCard className="h-4 w-4" /> Payment Information
                      </h3>
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Court Rental</span>
                          <span className={theme.text}>₱{formatCurrency(selectedKioskTransaction.total_amount || 0)}</span>
                        </div>
                        <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                          <span className={`${theme.textMuted} font-medium`}>Total Amount</span>
                          <span className="text-emerald-500 font-semibold text-lg">
                            ₱{formatCurrency(selectedKioskTransaction.total_amount || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Payment Method</span>
                          <span className={theme.text}>
                            {selectedKioskTransaction.payment_type === 'online' ? 'Credit/Debit Card' : 
                             selectedKioskTransaction.payment_type === 'counter' ? 'Counter Payment' :
                             selectedKioskTransaction.payment_type || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Payment Status</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedKioskTransaction.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedKioskTransaction.payment_status === 'reserved' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {selectedKioskTransaction.payment_status || 'confirmed'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons for Reserved Transactions */}
                    {selectedKioskTransaction.payment_status === 'reserved' && (
                      <div className="space-y-3">
                        {/* Pay Now Section */}
                        {!showKioskPaymentForm ? (
                          <Button 
                            size="sm" 
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => {
                              setSelectedKioskPaymentMethod('cash');
                              setShowKioskPaymentForm(true);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay Now
                          </Button>
                        ) : (
                          <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-3`}>
                            <Label className={`${theme.text} text-sm font-medium`}>Select Payment Method</Label>
                            <div className="grid grid-cols-4 gap-2">
                              {PAYMENT_METHODS.map((method) => {
                                const Icon = method.icon;
                                return (
                                  <button
                                    key={method.id}
                                    onClick={() => setSelectedKioskPaymentMethod(method.id)}
                                    className={`p-2 rounded-lg border text-center transition-colors ${
                                      selectedKioskPaymentMethod === method.id
                                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-500'
                                        : isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                    }`}
                                  >
                                    <Icon className="h-4 w-4 mx-auto mb-1" />
                                    <span className="text-xs">{method.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                                onClick={() => setShowKioskPaymentForm(false)}
                              >
                                Cancel
                              </Button>
                              <Button 
                                size="sm" 
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={kioskPaymentLoading}
                                onClick={async () => {
                                  const success = await confirmKioskPayment(
                                    selectedKioskTransaction.transaction_code,
                                    selectedKioskPaymentMethod
                                  );
                                  if (success) {
                                    setShowKioskPaymentForm(false);
                                    // Update local state
                                    setSelectedKioskTransaction(prev => ({
                                      ...prev,
                                      payment_status: 'paid',
                                      payment_type: selectedKioskPaymentMethod
                                    }));
                                    // Refresh the data
                                    fetchData();
                                  }
                                }}
                              >
                                {kioskPaymentLoading ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                  </div>
                                ) : (
                                  'Confirm Payment'
                                )}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Reschedule & Cancel Buttons */}
                        {/* <div className={`grid grid-cols-2 gap-2 pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                          <Button 
                            variant="outline" 
                            className={isDarkMode ? 'border-slate-600' : 'border-gray-300'} 
                            onClick={() => setShowKioskReschedule(true)}
                          >
                            <ArrowRightLeft className="h-4 w-4 mr-1" />
                            Reschedule
                          </Button>
                          <Button 
                            variant="outline" 
                            className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10" 
                            onClick={() => setShowKioskCancel(true)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Cancel Booking
                          </Button>
                        </div> */}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kiosk Reschedule Modal */}
        {/* {showKioskReschedule && selectedKioskTransaction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader>
                <CardTitle className={theme.text}>Reschedule Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className={`${theme.textMuted} text-sm`}>
                  Rescheduling for kiosk transactions must be done through the kiosk system.
                </p>
                <p className={`${theme.text} text-sm`}>
                  Booking ID: <span className="font-mono">{selectedKioskTransaction.transaction_code}</span>
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setShowKioskReschedule(false)}
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        )} */}

        {/* Kiosk Cancel Modal */}
        {/* {showKioskCancel && selectedKioskTransaction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader>
                <CardTitle className={theme.text}>Cancel Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className={`${theme.textMuted} text-sm`}>
                  Are you sure you want to cancel this booking?
                </p>
                <p className={`${theme.text} text-sm`}>
                  Booking ID: <span className="font-mono">{selectedKioskTransaction.transaction_code}</span>
                </p>
                <div>
                  <Label className={theme.text}>Cancellation Reason</Label>
                  <textarea
                    value={kioskCancelNote}
                    onChange={(e) => setKioskCancelNote(e.target.value)}
                    className={`w-full h-20 px-3 py-2 rounded-md resize-none mt-2 ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                    placeholder="Enter reason for cancellation..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setShowKioskCancel(false);
                      setKioskCancelNote('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={async () => {
                      toast.info('Cancellation for kiosk transactions must be processed through the kiosk system');
                      setShowKioskCancel(false);
                      setKioskCancelNote('');
                    }}
                  >
                    Confirm Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )} */}
      </div>
    </AdminLayout>
  );
};

export default AdminBookingsPage;
